// Bottom status bar + Alan Turing status face, drawn in screen space over the
// composited world and weapon. The face is our hero's likeness (stylized,
// respectful): 1940s side-parted hair, shirt collar and tie. It reacts to
// health and recent damage the way the classic id status face does. Only the
// passed 2D context is used, so this module is DOM-free.

export interface HudState {
  readonly health: number; // 0..100
  readonly armor: number; // 0..100
  readonly ammoInMag: number;
  readonly magazine: number;
  readonly reloading: boolean;
  readonly score: number;
  readonly enemiesLeft: number;
  readonly hurt: number; // 0..1 recent damage flash
  readonly elapsed: number;
  readonly reducedMotion: boolean;
}

const BAR_TOP = 644;
const PANEL = "rgba(8, 13, 20, 0.9)";
const ACCENT = "#ff7a1a";
const INK = "#f7fbff";
const DIM = "#9cb2bd";

export function drawHud(ctx: CanvasRenderingContext2D, s: HudState): void {
  // Low-health vignette (subtle, escalates as health falls).
  if (s.health <= 35) {
    const v = ctx.createRadialGradient(640, 360, 260, 640, 360, 760);
    const a = ((35 - s.health) / 35) * 0.5;
    v.addColorStop(0, "rgba(120,0,0,0)");
    v.addColorStop(1, `rgba(150,0,0,${a})`);
    ctx.fillStyle = v;
    ctx.fillRect(0, 0, 1280, 720);
  }
  // Damage flash across the whole view.
  if (s.hurt > 0) {
    ctx.fillStyle = `rgba(190,20,20,${s.hurt * 0.4})`;
    ctx.fillRect(0, 0, 1280, 720);
  }

  // Crosshair (kept crisp and central; opens up while reloading).
  drawCrosshair(ctx, s.reloading);

  // Status bar backing.
  ctx.fillStyle = PANEL;
  ctx.fillRect(0, BAR_TOP, 1280, 720 - BAR_TOP);
  ctx.strokeStyle = "rgba(255,122,26,0.5)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, BAR_TOP + 1);
  ctx.lineTo(1280, BAR_TOP + 1);
  ctx.stroke();

  const midY = BAR_TOP + (720 - BAR_TOP) / 2;

  // Left cluster: HEALTH + ARMOR.
  drawStat(ctx, 40, BAR_TOP + 14, "HEALTH", Math.round(s.health), healthColor(s.health), s.health / 100);
  drawStat(ctx, 40, BAR_TOP + 44, "ARMOR", Math.round(s.armor), "#3fb7ff", s.armor / 100);

  // Center: Turing face in a recessed bezel + nameplate.
  const faceCx = 640;
  const faceCy = midY - 4;
  ctx.fillStyle = "rgba(0,0,0,0.5)";
  roundRect(ctx, faceCx - 46, BAR_TOP + 6, 92, 720 - BAR_TOP - 10, 8);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 2;
  roundRect(ctx, faceCx - 46, BAR_TOP + 6, 92, 720 - BAR_TOP - 10, 8);
  ctx.stroke();
  drawTuringFace(ctx, faceCx, faceCy - 2, 0.82, s);
  setText(ctx, 10, 800, ACCENT, "center");
  ctx.fillText("TURING", faceCx, 720 - 12);

  // Right cluster: AMMO + SCORE + enemies.
  const rightX = 1240;
  setText(ctx, 12, 800, DIM, "right");
  ctx.fillText("AMMO", rightX, BAR_TOP + 20);
  setText(ctx, 30, 900, s.reloading ? ACCENT : INK, "right");
  ctx.fillText(s.reloading ? "RELOAD" : `${s.ammoInMag}/${s.magazine}`, rightX, BAR_TOP + 44);
  setText(ctx, 12, 800, DIM, "right");
  ctx.fillText("SCORE", rightX - 190, BAR_TOP + 20);
  setText(ctx, 30, 900, INK, "right");
  ctx.fillText(String(s.score), rightX - 190, BAR_TOP + 44);
  setText(ctx, 11, 700, DIM, "right");
  ctx.fillText(`HOSTILES ${s.enemiesLeft}`, rightX, 720 - 12);
}

function drawStat(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  label: string,
  value: number,
  color: string,
  frac: number,
): void {
  setText(ctx, 12, 800, DIM, "left");
  ctx.fillText(label, x, y);
  setText(ctx, 26, 900, color, "left");
  ctx.fillText(String(value), x + 96, y + 2);
  // Bar.
  const bx = x;
  const by = y + 14;
  ctx.fillStyle = "rgba(255,255,255,0.12)";
  roundRect(ctx, bx, by, 150, 6, 3);
  ctx.fill();
  ctx.fillStyle = color;
  roundRect(ctx, bx, by, 150 * Math.max(0, Math.min(1, frac)), 6, 3);
  ctx.fill();
}

function healthColor(h: number): string {
  if (h > 60) return "#42e2c3";
  if (h > 30) return "#ffd23b";
  return "#ff5d5d";
}

function drawCrosshair(ctx: CanvasRenderingContext2D, reloading: boolean): void {
  const gap = reloading ? 16 : 8;
  const len = 10;
  ctx.strokeStyle = "rgba(247,251,255,0.85)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(640 - gap - len, 360);
  ctx.lineTo(640 - gap, 360);
  ctx.moveTo(640 + gap, 360);
  ctx.lineTo(640 + gap + len, 360);
  ctx.moveTo(640, 360 - gap - len);
  ctx.lineTo(640, 360 - gap);
  ctx.moveTo(640, 360 + gap);
  ctx.lineTo(640, 360 + gap + len);
  ctx.stroke();
  ctx.fillStyle = "rgba(247,251,255,0.9)";
  ctx.fillRect(639, 359, 2, 2);
}

// ---------------------------------------------------------------------------
// Turing status face
// ---------------------------------------------------------------------------

export function drawTuringFace(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  scale: number,
  s: Pick<HudState, "health" | "hurt" | "elapsed" | "reducedMotion">,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(scale, scale);

  const dead = s.health <= 0;
  const h = s.health;
  // Slow left/right glance when healthy (charm), frozen when hurt/reduced.
  const glance =
    s.reducedMotion || s.hurt > 0.2 || dead ? 0 : Math.round(Math.sin(s.elapsed * 0.8)) * 2;

  // Shoulders: shirt collar + tie (1940s).
  ctx.fillStyle = "#dfe6ee"; // shirt
  roundRect(ctx, -46, 34, 92, 40, 10);
  ctx.fill();
  ctx.fillStyle = "#c3ccd6";
  // collar points
  ctx.beginPath();
  ctx.moveTo(-14, 36);
  ctx.lineTo(0, 58);
  ctx.lineTo(-22, 52);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(14, 36);
  ctx.lineTo(0, 58);
  ctx.lineTo(22, 52);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#3a4a63"; // tie
  ctx.beginPath();
  ctx.moveTo(0, 46);
  ctx.lineTo(-6, 58);
  ctx.lineTo(0, 74);
  ctx.lineTo(6, 58);
  ctx.closePath();
  ctx.fill();

  // Neck.
  ctx.fillStyle = "#d29a72";
  roundRect(ctx, -12, 24, 24, 20, 6);
  ctx.fill();

  // Head.
  const skin = s.hurt > 0 ? "#f0b48c" : "#e7b48c";
  ctx.fillStyle = skin;
  ctx.beginPath();
  ctx.ellipse(0, 0, 34, 40, 0, 0, Math.PI * 2);
  ctx.fill();
  // Ears.
  ctx.beginPath();
  ctx.ellipse(-33, 2, 6, 9, 0, 0, Math.PI * 2);
  ctx.ellipse(33, 2, 6, 9, 0, 0, Math.PI * 2);
  ctx.fill();
  // Jaw shading.
  ctx.fillStyle = "rgba(150,90,55,0.25)";
  ctx.beginPath();
  ctx.ellipse(0, 20, 24, 18, 0, 0, Math.PI);
  ctx.fill();

  // Hair: dark, side-parted (parting on his left = viewer right), swept.
  ctx.fillStyle = "#4a3524";
  ctx.beginPath();
  ctx.moveTo(-34, -6);
  ctx.quadraticCurveTo(-38, -40, -6, -42);
  ctx.quadraticCurveTo(30, -44, 34, -14);
  ctx.quadraticCurveTo(20, -30, 6, -28);
  ctx.quadraticCurveTo(-6, -26, -12, -20);
  ctx.quadraticCurveTo(-20, -12, -34, -6);
  ctx.closePath();
  ctx.fill();
  // Fringe sweep highlight.
  ctx.strokeStyle = "#5c4531";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(-8, -34);
  ctx.quadraticCurveTo(10, -30, 24, -18);
  ctx.stroke();

  if (dead) {
    // Deceased: X eyes, slack mouth, pale.
    ctx.strokeStyle = "#3a2a20";
    ctx.lineWidth = 2.5;
    for (const ex of [-14, 14]) {
      ctx.beginPath();
      ctx.moveTo(ex - 5, -8);
      ctx.lineTo(ex + 5, 2);
      ctx.moveTo(ex + 5, -8);
      ctx.lineTo(ex - 5, 2);
      ctx.stroke();
    }
    ctx.fillStyle = "#7a2b2b";
    roundRect(ctx, -10, 16, 20, 5, 2);
    ctx.fill();
    ctx.restore();
    return;
  }

  // Eyebrows: lower/angrier as health drops.
  const brow = h > 60 ? 0 : h > 30 ? 3 : 6;
  ctx.strokeStyle = "#4a3524";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-22, -12 + brow);
  ctx.lineTo(-6, -14 + brow * 0.4);
  ctx.moveTo(22, -12 + brow);
  ctx.lineTo(6, -14 + brow * 0.4);
  ctx.stroke();

  // Eyes.
  ctx.fillStyle = "#fbfdff";
  ctx.beginPath();
  ctx.ellipse(-14, -4, 7, 5, 0, 0, Math.PI * 2);
  ctx.ellipse(14, -4, 7, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#2a2320";
  ctx.beginPath();
  ctx.arc(-14 + glance, -4, 2.8, 0, Math.PI * 2);
  ctx.arc(14 + glance, -4, 2.8, 0, Math.PI * 2);
  ctx.fill();

  // Nose.
  ctx.strokeStyle = "rgba(150,90,55,0.5)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, -2);
  ctx.lineTo(-3, 10);
  ctx.lineTo(2, 11);
  ctx.stroke();

  // Mouth: smile -> neutral -> grimace as health falls.
  ctx.strokeStyle = "#7a3b32";
  ctx.lineWidth = 3;
  ctx.beginPath();
  if (h > 75) {
    ctx.moveTo(-11, 18);
    ctx.quadraticCurveTo(0, 25, 11, 18); // faint smile
  } else if (h > 45) {
    ctx.moveTo(-11, 20);
    ctx.lineTo(11, 20); // neutral
  } else if (h > 20) {
    ctx.moveTo(-11, 22);
    ctx.quadraticCurveTo(0, 16, 11, 22); // strained
  } else {
    // gritted teeth
    ctx.moveTo(-12, 18);
    ctx.lineTo(12, 18);
    ctx.moveTo(-12, 23);
    ctx.lineTo(12, 23);
    ctx.moveTo(-7, 18);
    ctx.lineTo(-7, 23);
    ctx.moveTo(0, 18);
    ctx.lineTo(0, 23);
    ctx.moveTo(7, 18);
    ctx.lineTo(7, 23);
  }
  ctx.stroke();

  // Sweat when strained.
  if (h <= 45 && h > 0) {
    ctx.fillStyle = "rgba(150,200,255,0.8)";
    ctx.beginPath();
    ctx.ellipse(-26, -6, 2.4, 4, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  // Blood when badly hurt.
  if (h <= 30 || s.hurt > 0.4) {
    ctx.strokeStyle = "#b3241d";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(18, -18);
    ctx.lineTo(24, -2);
    ctx.stroke();
    if (h <= 20) {
      ctx.fillStyle = "rgba(150,20,20,0.35)";
      ctx.beginPath();
      ctx.ellipse(0, 4, 30, 34, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Shared helpers (self-contained so the module has no cross-imports).
// ---------------------------------------------------------------------------

function setText(
  ctx: CanvasRenderingContext2D,
  size: number,
  weight: number,
  color: string,
  align: CanvasTextAlign,
): void {
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "alphabetic";
  ctx.font = `${weight} ${size}px "Avenir Next", "Helvetica Neue", sans-serif`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
